import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonExtendedDataListEditView } from './views/EditableContentForm';
import {
  BlockInteractions,
  createButtonObject,
  DISPLAY_TYPE,
  Views as BlockInteractionsViews,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { BlockType } from '../../types/api/ElementType';
import { PersonExtendedDataListDefaultView } from './views/PersonExtendedDataList';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { DefaultView, OptionalEditView, Views } from '../../types/BlockWithViews';
import { getTranslationNamespaceByElementType } from './PersonExtendedDataBlock.utils';
import { useAppContext } from '../../contexts/AppContext/AppContext';

type PersonExtendedData = Views<
  DefaultView<PersonExtendedDataListDefaultView> & OptionalEditView<PersonExtendedDataListEditView>
>;

export function PersonExtendedData({ views, activeView }: PersonExtendedData) {
  const { t } = useTranslation();
  const { editModeInfo } = useAppContext();
  const { elementType, uuid } = useBlockContext();
  const { refreshCount } = useRefreshBlock();

  const getHeadlinehierarchy = () => {
    switch (elementType) {
      case BlockType.personaffiliation:
      case BlockType.personcontactdata:
        return 2;
      case BlockType.personextendeddata:
      case BlockType.personadditionalinformation:
      default:
        return 3;
    }
  };

  const buttons = views.edit
    ? {
        ...createButtonObject(
          BlockInteractionsViews.EDIT,
          Icons.EDIT_PENCIL,
          `${getTranslationNamespaceByElementType(elementType)}:actions.edit`,
          DISPLAY_TYPE.COLLAPSIBLE,
          <PersonExtendedDataListEditView {...views.edit} />
        ),
      }
    : null;

  return (
    <BlockInteractions
      key={editModeInfo.active ? `${uuid}-page-edit-mode` : `${uuid}-page-default-view`}
      headline={{
        text: t(`${getTranslationNamespaceByElementType(elementType)}:headline`),
        hierarchy: getHeadlinehierarchy(),
        visible: true,
      }}
      alwaysShowInfo
      views={views}
      activeView={activeView}
      buttons={buttons}
      uuid={uuid}
      elementType={elementType}
      refreshCount={refreshCount}
    >
      <PersonExtendedDataListDefaultView {...views.default} />
    </BlockInteractions>
  );
}

type PersonExtendedDataBlockTypes =
  | BlockType.personextendeddata
  | BlockType.personcontactdata
  | BlockType.personadditionalinformation
  | BlockType.personaffiliation;

export const PersonExtendedDataBlock = asFetchableBlock<PersonExtendedData, PersonExtendedDataBlockTypes>(
  PersonExtendedData
);
export type PersonExtendedDataBlock = ComponentProps<typeof PersonExtendedDataBlock>;
